import { renderProviderImage } from '@vgrubs/components';
// Straight from the files: the shared logo helper swaps these two for the vGrubs AI logo on
// partner pages, and here they are marketplaces of their own.
import bytesLogo from '../../../assets/images/providers/bytes.png';
import kanekt365Logo from '../../../assets/images/providers/kanekt.png';
import { IntegrationAccessProvider } from '../../../types/api/integrationAccess.types';
import { MenuSignInProviderEntity } from '../../../types/partner/locations.partner-types';
import { Provider } from '../../../types/shared.types';

/**
 * The marketplaces Integration Access offers, in the order the partner sees them: the
 * Manage Providers list without the POS twins, vGrubs, vGrubs AI and Delivery.com, the same
 * as the merchant app. Labels are written out because the shared name helper says
 * "vGrubs AI" for Bytes and Kanekt on partner pages.
 */
export const INTEGRATION_MARKETPLACES: {
  type: Provider;
  label: string;
  logo?: boolean;
}[] = [
  { type: Provider.ubereats, label: 'Uber Eats', logo: true },
  { type: Provider.grubhub, label: 'Grubhub', logo: true },
  { type: Provider.doordash, label: 'DoorDash', logo: true },
  { type: Provider.chownow, label: 'ChowNow' },
  { type: Provider.gloriafood, label: 'GloriaFood' },
  { type: Provider.eatstreet, label: 'EatStreet' },
  { type: Provider.menufy, label: 'Menufy' },
  { type: Provider.localForYou, label: 'Local For You' },
  { type: Provider.nabe, label: 'Nabe' },
  { type: Provider.bytes, label: 'Bytes AI' },
  { type: Provider.kanekt365, label: 'Kanekt 365' },
  { type: Provider.devourNow, label: 'DevourNow' },
];

export type MarketplaceTile = {
  key: string;
  /** Provider type, for the logo and the steps; null for a leftover without one. */
  type: string | null;
  label: string;
  logo: boolean;
  /** The menu-providers row the selection is saved under; null = not offered by the API yet. */
  menuProviderId: number | null;
};

const normalize = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const LISTED_TYPES = new Set<string>(INTEGRATION_MARKETPLACES.map((m) => m.type));

/**
 * The logo for a marketplace, by its `type`; by name when there is none (older backend, or a
 * legacy row). Never by name alone when a type is known: the backend names rows "Uber Eats",
 * and "uber eats" matches no logo. Bytes and Kanekt get their own logos, not vGrubs AI's.
 */
export const marketplaceLogo = (
  type?: string | null,
  name?: string | null
): string | undefined => {
  if (type === Provider.bytes) return bytesLogo;
  if (type === Provider.kanekt365) return kanekt365Logo;
  const key = type || (name ? normalize(name) : '');
  return (key && renderProviderImage(key)) || undefined;
};

/**
 * The tiles to show, each resolved to its row in GET /v1/locations/menu-providers. A row is
 * matched by `type`; a row the API sends without one (older backend) is matched by name.
 * A marketplace with no row yet still shows, disabled, so the list reads the same everywhere.
 * Anything the partner selected that is not in the list (picked before it existed) is added
 * at the end, so it can still be opened and removed.
 */
export const buildMarketplaceTiles = (
  providers: MenuSignInProviderEntity[],
  selected: IntegrationAccessProvider[]
): MarketplaceTile[] => {
  const used = new Set<number>();

  const tiles = INTEGRATION_MARKETPLACES.map((m) => {
    const names = [normalize(m.label), normalize(m.type)];
    // By type first; a row with no type, or one the app does not know, by name.
    const row =
      providers.find((p) => p.type === m.type) ??
      providers.find(
        (p) =>
          (!p.type || !LISTED_TYPES.has(p.type)) &&
          names.includes(normalize(p.name))
      );
    if (row) used.add(row.id);
    return {
      key: m.type,
      type: m.type,
      label: m.label,
      logo: !!m.logo,
      menuProviderId: row?.id ?? null,
    };
  });

  const leftovers = selected
    .filter((s) => !used.has(s.menuProviderId))
    .map((s) => ({
      key: `selected-${s.menuProviderId}`,
      type:
        s.type ??
        providers.find((p) => p.id === s.menuProviderId)?.type ??
        null,
      label: s.name,
      logo: false,
      menuProviderId: s.menuProviderId,
    }));

  return [...tiles, ...leftovers];
};
